/**
 * PropertyOverview Component
 * 
 * Core component for displaying property overview and description
 */

'use client';

import type { Property } from '@/lib/api/clients/properties';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { MarkdownRenderer } from '@routeroyal/ui-core';

export interface PropertyOverviewProps {
  property: Property;
  messages: {
    overview: {
      title: string;
      description: string;
      status: string;
      propertyId: string;
    };
    pricing: {
      askingPrice: string;
      contactForPrice: string;
    };
  };
  /** @deprecated Use price and currency instead for currency conversion support */
  formattedPrice?: string | null;
  /** Price amount for currency-aware display */
  price?: number | null;
  /** Currency code (ISO 4217) */
  currency?: string;
  /** Listing type badge (SALE, RENTAL, AUCTION, etc.) */
  listingType?: string | null;
  /** Custom headline from the listing */
  mainHeadline?: string | null;
  /** Custom price display text from the listing */
  displayPriceText?: string | null;
  /** Listing status (ACTIVE, SOLD, etc.) */
  listingStatus?: string | null;
  className?: string;
}

const LISTING_TYPE_LABELS: Record<string, string> = {
  SALE: 'For Sale',
  RENTAL: 'For Rent',
  AUCTION: 'Auction',
  TENDER: 'Tender',
  EXPRESSION_OF_INTEREST: 'EOI',
  LEASE: 'For Lease',
  COMMERCIAL_SALE: 'Commercial Sale',
  COMMERCIAL_RENTAL: 'Commercial Rental',
  COMMERCIAL_LEASE: 'Commercial Lease',
  HOLIDAY_RENTAL: 'Holiday Rental',
};

const LISTING_TYPE_COLOURS: Record<string, string> = {
  SALE: 'bg-emerald-600',
  RENTAL: 'bg-blue-600',
  AUCTION: 'bg-amber-600',
  TENDER: 'bg-purple-600',
  LEASE: 'bg-blue-600',
};

export function PropertyOverview({
  property,
  messages,
  formattedPrice,
  price,
  currency = 'USD',
  listingType,
  mainHeadline,
  displayPriceText,
  listingStatus,
  className = ''
}: PropertyOverviewProps) {
  const isForeignerEligible = property.attributeValues?.foreignOwnershipAllowed?.valueBoolean ?? false;
  const taxScheme = property.attributeValues?.taxScheme?.valueString || null;
  const propertyTypeName = property.propertyType?.name || null;

  return (
    <div className={`font-inter ${className}`}>
      {/* Property type and reference line */}
      <div className="flex items-center gap-3 text-sm text-[#717171] mb-3 flex-wrap">
        {propertyTypeName && (
          <span className="font-medium text-[#222222]">{propertyTypeName}</span>
        )}
        {propertyTypeName && property.reference && <span>·</span>}
        {property.reference && (
          <span className="font-mono tracking-wide">{property.reference}</span>
        )}
      </div>

      {/* Listing headline (if provided by listing, shown above property name) */}
      {mainHeadline && (
        <p className="text-lg text-[#717171] mb-2 leading-relaxed">{mainHeadline}</p>
      )}

      {/* Property Name */}
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#222222] mb-4 leading-tight font-inter">
        {property.name}
      </h1>

      {/* Badges: listing type, property tags */}
      {(listingType || isForeignerEligible || taxScheme) && (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          {listingType && (
            <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold text-white ${
              listingStatus === 'SOLD' ? 'bg-gray-700' : (LISTING_TYPE_COLOURS[listingType] || 'bg-gray-600')
            }`}>
              {listingStatus === 'SOLD' ? 'Sold' : (LISTING_TYPE_LABELS[listingType] || listingType)}
            </span>
          )}
          {isForeignerEligible && (
            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-[#243462] text-white">
              Available for foreigners
            </span>
          )}
          {taxScheme && (
            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-[#BBA339] text-white uppercase">
              {taxScheme}
            </span>
          )}
        </div>
      )}

      {/* Price */}
      {(price || formattedPrice) ? (
        <div className="mb-4">
          <div className="text-2xl font-semibold text-[#222222]">
            {price ? (
              <FormatPrice 
                amount={price} 
                currency={currency} 
                size="lg"
                priceOnRequestText={messages.pricing.contactForPrice}
              />
            ) : (
              formattedPrice
            )}
          </div>
          {displayPriceText && (
            <p className="text-sm text-[#717171] mt-1">{displayPriceText}</p>
          )}
        </div>
      ) : (
        <div className="mb-4">
          <p className="text-lg font-medium text-[#222222]">
            {displayPriceText || messages.pricing.contactForPrice}
          </p>
        </div>
      )}

      {/* Basic Info - Under price */}
      <div className="flex items-center gap-3 text-base text-[#717171] mb-6">
        {property.bedrooms && (
          <span className="font-medium">{property.bedrooms} bed{property.bedrooms !== 1 ? 's' : ''}</span>
        )}
        {property.bedrooms && property.bathrooms && <span>·</span>}
        {property.bathrooms && (
          <span className="font-medium">{property.bathrooms} bath{property.bathrooms !== 1 ? 's' : ''}</span>
        )}
        {(property.bedrooms || property.bathrooms) && property.totalFloorArea && <span>·</span>}
        {property.totalFloorArea && (
          <span className="font-medium">{property.totalFloorArea} {property.totalFloorAreaUnit?.toLowerCase() || 'sqft'}</span>
        )}
      </div>

      {/* Description */}
      {property.description && (
        <div className="property-summary mb-6" data-speakable>
          <MarkdownRenderer
            content={property.description}
            theme="custom"
            className="prose prose-base max-w-none text-[#222222] leading-relaxed prose-headings:text-[#222222] prose-p:text-[#222222] prose-p:leading-relaxed prose-strong:text-[#222222] prose-li:text-[#222222] prose-ul:my-3 prose-ol:my-3 prose-p:my-3"
          />
        </div>
      )}

    </div>
  );
}
